import React from "react";

import { valueFormat } from "../../../../common/src/utils/FormatFunctions";
import { usePerformance } from "./performanceGraphHooks";
import { percentageFormat, yearFracActual365 } from "./functions";
import { SelectedRowType } from "./interfaces";

interface KpiCardProps {
    label: string;
    value: React.ReactNode;
    tone?: "neutral" | "positive" | "negative";
    hint?: React.ReactNode;
    wrapLabel?: boolean;
}

const KpiCard = ({ label, value, tone = "neutral", hint, wrapLabel }: KpiCardProps): React.ReactElement => {
    const valueColor = tone === "positive" ? "text-emerald-700" : tone === "negative" ? "text-rose-700" : "text-slate-900";
    const labelClass = wrapLabel
        ? "text-[11px] text-slate-500 font-medium leading-snug whitespace-normal break-words"
        : "text-[11px] text-slate-500 font-medium";
    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className={labelClass} title={label}>
                {label}
            </div>
            <div className={`mt-1 text-2xl font-semibold tabular-nums leading-tight ${valueColor}`}>{value}</div>
            {hint ? <div className="mt-1 text-xs text-slate-500">{hint}</div> : null}
        </div>
    );
};

const formatSignedPct = (value: number | null): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return "—";
    const sign = value >= 0 ? "+" : "";
    return sign + percentageFormat(value);
};

const annualizeReturn = (totalReturn: number | null, startDate: string, endDate: string): number | null => {
    if (totalReturn === null || totalReturn === undefined || Number.isNaN(totalReturn)) return null;
    const yearFrac = yearFracActual365(new Date(startDate), new Date(endDate));
    return Math.pow(1 + totalReturn, 1 / yearFrac) - 1;
};

const returnTone = (annualized: number | null, total: number | null): "neutral" | "positive" | "negative" => {
    let value = annualized;
    if (value === null) value = total;
    if (value === null || Number.isNaN(value)) return "neutral";
    if (value >= 0) return "positive";
    return "negative";
};

const KpiPeriodValue = ({ total, annualized }: { total: number | null; annualized: number | null }): React.ReactElement => {
    if (annualized !== null) {
        return (
            <span>
                {formatSignedPct(annualized)}
                <span className="ml-1 text-lg font-normal">p.a.</span>
                {total !== null ? <span className="ml-1 text-base font-normal text-slate-400">({formatSignedPct(total)})</span> : null}
            </span>
        );
    }
    return <span>{formatSignedPct(total)}</span>;
};

export function PerformanceKpiStrip({ selectedRow }: { selectedRow: SelectedRowType }): React.ReactElement {
    const { alignedTimeSeries, value } = usePerformance(selectedRow);

    const accountSeries = alignedTimeSeries[0];

    // Mirror the precedence used by the table return columns (benchmarkReturn): prefer the model-index
    // series when one was included for the selected row, otherwise fall back to the main benchmark
    // (first benchmark series). `selectedRow.modelBenchmarkName` is only set when the model index was
    // actually added — which already accounts for the NaN / instrument-row cases where the table also
    // falls back to the main benchmark.
    const modelBenchmarkSeries = selectedRow.modelBenchmarkName
        ? alignedTimeSeries.find((ts) => ts && ts.name === selectedRow.modelBenchmarkName)
        : undefined;
    const benchmarkSeries = modelBenchmarkSeries ?? alignedTimeSeries[1];

    const accountRet =
        accountSeries && accountSeries.__values && accountSeries.__values.length
            ? accountSeries.__values[accountSeries.__values.length - 1] / 100 - 1
            : null;
    const benchmarkRet =
        benchmarkSeries && benchmarkSeries.__values && benchmarkSeries.__values.length
            ? benchmarkSeries.__values[benchmarkSeries.__values.length - 1] / 100 - 1
            : null;
    const diffRet = accountRet !== null && benchmarkRet !== null ? accountRet - benchmarkRet : null;

    const accountRetAnnualized = annualizeReturn(accountRet, selectedRow.startDate, selectedRow.endDate);
    const benchmarkRetAnnualized = annualizeReturn(benchmarkRet, selectedRow.startDate, selectedRow.endDate);
    const diffRetAnnualized =
        accountRetAnnualized !== null && benchmarkRetAnnualized !== null ? accountRetAnnualized - benchmarkRetAnnualized : null;

    const accountTone = returnTone(accountRetAnnualized, accountRet);
    const diffTone = returnTone(diffRetAnnualized, diffRet);
    let benchmarkLabel = "Benchmark";
    if (benchmarkSeries) {
        const seriesName = benchmarkSeries.name || ("label" in benchmarkSeries ? String(benchmarkSeries.label || "") : "");
        if (seriesName) benchmarkLabel = seriesName;
    }

    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
                label="Value (SEK)"
                value={typeof value === "number" ? valueFormat(value) : "—"}
                hint={selectedRow.name ? <span className="truncate">{selectedRow.name}</span> : null}
            />
            <KpiCard label="Portfolio" value={<KpiPeriodValue total={accountRet} annualized={accountRetAnnualized} />} tone={accountTone} />
            <KpiCard
                label={benchmarkLabel}
                value={<KpiPeriodValue total={benchmarkRet} annualized={benchmarkRetAnnualized} />}
                wrapLabel={benchmarkLabel !== "Benchmark"}
            />
            <KpiCard label="Difference" value={<KpiPeriodValue total={diffRet} annualized={diffRetAnnualized} />} tone={diffTone} />
        </div>
    );
}
